import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Tag, RedDot, Reveal } from "../components/ui-primitives";
import { Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATS = [
  { label: "Years in Operation", value: "17" },
  { label: "Active Clients", value: "150+" },
  { label: "Media Placed Annually", value: "₹200+ Cr" },
  { label: "Media Partner Relationships", value: "1,000+" },
];

// Bulleted facts — red dot motif as bullets.
const CONTEXT_BULLETS = [
  [
    "INS-accredited, DAVP-empanelled full-service advertising agency",
    "Incorporated 2008, Ludhiana",
    "Public limited company since June 2024",
    "Seven offices: Delhi, Mumbai, Chandigarh, Jaipur, Lucknow, Shimla, Ludhiana",
  ],
  [
    "₹200+ Cr in annual media placements (FY2024)",
    "150+ clients across Healthcare, FMCG, Education, Automobile, Real Estate, Jewellery, and Government",
    "1,000+ media partner relationships across 18 languages",
    "Group includes Hype N Hike Advertising (wholly owned subsidiary) and Transmitter Branding Agency (51% stake)",
  ],
  [
    "Average client tenure of 7 years; several key accounts over a decade",
    "Annual deal structures with media partners create commercial advantages not replicable by direct buying",
    "Public company at an inflection point: established market position, actively expanding capability",
  ],
];

// Competitive Position — 6 tiles, each with max 3 bullets. Card 01 has a bold
// stat headline above the bullets. Mobile uses a single-word pill label.
const POSITION_TILES = [
  {
    id: "scale-led-buying",
    pill: "Scale",
    title: "Scale-led buying",
    bullets: [
      "Aggregate buying across 150+ clients creates negotiating leverage no single advertiser can match",
      "Annual contracts with media houses lock in preferential pricing and inventory before the market moves",
      "7-year average client tenure ensures that continuity compounds the commercial advantage over time",
    ],
  },
  {
    id: "execution-discipline",
    pill: "Discipline",
    title: "Execution discipline",
    bullets: [
      "MIS confirmation of every print placement by 10:00 AM on the day of publication",
      "End-to-end campaign audit trails as standard — not on request",
      "Proof-of-execution reporting for OOH and cinema placements",
    ],
  },
  {
    id: "institutional-access",
    pill: "Access",
    title: "Institutional access",
    bullets: [
      "Government advertising mandates are a distinct, recurring revenue stream unavailable to most private-sector agencies",
      "DAVP, UPID, and Rajasthan Samvad empanelment provides access to central and state-level statutory advertising — a captive category",
      "Formal accreditation requirements mean few competitors can enter this segment",
    ],
  },
  {
    id: "integrated-capability",
    pill: "Capability",
    title: "Integrated capability",
    bullets: [
      "Single-point client interface across media planning, creative, and hyperlocal execution",
      "In-house creative arm: Transmitter Branding Agency (51% stake)",
      "Hyperlocal and retail capability: Hype N Hike Advertising (wholly owned subsidiary)",
    ],
  },
  {
    id: "vernacular-depth",
    pill: "Vernacular",
    title: "Vernacular depth",
    bullets: [
      "Active buying in 18 languages across 1,000+ media partners",
      "Structural access to Tier 2, Tier 3, and rural India",
      "We are present where the next wave of Indian consumer growth is concentrated",
    ],
  },
  {
    id: "asset-light",
    pill: "Partnerships",
    title: "Asset-light execution",
    bullets: [
      "Full-spectrum campaign delivery at scale without the balance sheet of a large network agency",
      "Specialist partner network for OOH, cinema, airport, auto branding, regional ambient formats, and Government advertising beyond DAVP, UP and Rajasthan",
      "Dedicated vendors per channel — quality control without fixed overhead",
    ],
  },
];

// Small red-dot bulleted list, reused in Business Context and the
// Competitive Position cards. Keeps visual consistency with the rest of
// the site's red-dot motif.
function BulletList({ items, color = "var(--color-on-surface-variant)" }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }} className="space-y-3">
      {items.map((b, i) => (
        <li key={typeof b === "string" ? b : `bullet-${i}`} className="flex items-start gap-3 body-md" style={{ color }}>
          <span
            aria-hidden
            style={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: "var(--color-tertiary)",
              display: "inline-block",
              flexShrink: 0,
              marginTop: 10,
            }}
          />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function PositionCard({ tile, idx }) {
  return (
    <div
      data-testid={`position-tile-${tile.id}`}
      style={{
        background: "var(--color-surface-container-lowest)",
        padding: "var(--space-32)",
        height: "100%",
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <RedDot />
        <span className="label-sm tabular" style={{ color: "var(--color-tertiary)" }}>
          {String(idx + 1).padStart(2, "0")}
        </span>
      </div>
      <h3 className="headline-md" style={{ marginBottom: 16, color: "var(--color-primary)" }}>
        {tile.title}
      </h3>
      {tile.stat && (
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: "clamp(1.125rem, 1.3vw, 1.375rem)",
            color: "var(--color-primary)",
            letterSpacing: "-0.01em",
            marginBottom: 16,
          }}
        >
          {tile.stat}
        </div>
      )}
      <BulletList items={tile.bullets} />
    </div>
  );
}

// Mobile-only pill/tab pattern for Competitive Position. One card visible at
// a time. Default tile 0.
function PositionMobile() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = POSITION_TILES[activeIdx];

  return (
    <div data-testid="position-mobile" className="md:hidden">
      <div
        data-testid="position-pill-grid"
        className="grid grid-cols-3"
        style={{ gap: 12 }}
      >
        {POSITION_TILES.map((t, i) => {
          const isActive = i === activeIdx;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`position-pill-${t.id}`}
              onClick={() => setActiveIdx(i)}
              aria-pressed={isActive}
              className="label-md"
              style={{
                padding: "10px 6px",
                borderRadius: "var(--radius-pill)",
                cursor: "pointer",
                background: isActive ? "var(--color-tertiary)" : "transparent",
                color: isActive ? "var(--color-on-tertiary)" : "var(--color-on-surface)",
                border: isActive ? "1px solid var(--color-tertiary)" : "1px solid var(--color-outline)",
                fontWeight: 600,
                letterSpacing: "0.04em",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                transition: "background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease",
              }}
            >
              {t.pill}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 24 }}>
        <PositionCard tile={active} idx={activeIdx} />
      </div>
    </div>
  );
}

export default function InvestorsPage() {
  const [form, setForm] = useState({
    full_name: "",
    organisation: "",
    role: "",
    email: "",
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.organisation || !form.role || !form.email || !form.note) {
      toast.error("Please fill out all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/investors/request`, form);
      setSubmitted(true);
      toast.success("Request submitted. Our leadership team will be in touch.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="investors-page">
      <Toaster position="top-center" richColors />

      {/* Hero — navy. H1 → sub-headline → stats grid. */}
      <section
        style={{
          background: "var(--color-primary)",
          color: "var(--color-on-primary)",
          paddingTop: 152,
          paddingBottom: 96,
        }}
      >
        <div className="di-container">
          <Tag light>Investor Relations</Tag>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h1
                className="display-lg"
                style={{
                  color: "var(--color-on-primary)",
                  fontSize: "clamp(2rem, 4.2vw, 3.75rem)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.025em",
                  marginBottom: 24,
                }}
              >
                Structured for scale.
                <br />
                Since 2008.
              </h1>
              <p
                data-testid="investors-hero-subhead"
                style={{
                  color: "rgba(249,249,248,0.78)",
                  fontSize: "clamp(1rem, 1.2vw, 1.25rem)",
                  lineHeight: 1.5,
                  maxWidth: 640,
                  marginBottom: 8,
                }}
              >
                A profitable, public-limited advertising company with 17 years of
                institutional relationships — seeking the right partner to scale.
              </p>
            </div>
            <div
              className="lg:col-span-5 grid grid-cols-2"
              style={{ gap: "1px", background: "rgba(249,249,248,0.1)" }}
            >
              {STATS.map((s) => (
                <div
                  key={s.label}
                  style={{ background: "var(--color-primary)", padding: "var(--space-24)" }}
                >
                  <div
                    className="label-sm"
                    style={{ color: "rgba(249,249,248,0.45)", marginBottom: 12 }}
                  >
                    {s.label}
                  </div>
                  <div
                    className="tabular"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "clamp(1.625rem, 3.2vw, 2.25rem)",
                      fontWeight: 800,
                      letterSpacing: "-0.03em",
                      color: "var(--color-on-primary)",
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.value}
                  </div>
                  {s.note && (
                    <div className="label-sm mt-2" style={{ color: "rgba(249,249,248,0.45)" }}>
                      {s.note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Business context — bulleted facts */}
      <section className="di-section" style={{ background: "var(--color-surface)" }}>
        <div className="di-container">
          <Tag>Business Context</Tag>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-2">
            {CONTEXT_BULLETS.map((items, i) => (
              <Reveal key={`context-${i + 1}`} delay={i * 80}>
                <div
                  data-testid={`context-block-${i + 1}`}
                  style={{
                    background: "var(--color-surface-container-lowest)",
                    padding: "var(--space-24)",
                    height: "100%",
                  }}
                >
                  <span
                    className="tabular"
                    style={{
                      color: "var(--color-tertiary)",
                      fontFamily: "var(--font-display)",
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      display: "inline-block",
                      marginBottom: 20,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <BulletList items={items} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive position — desktop: 3+3 grid; mobile: pill/tab pattern */}
      <section
        data-testid="competitive-position-section"
        className="di-section"
        style={{ background: "var(--color-surface-container-low)" }}
      >
        <div className="di-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-7">
              <Tag>Competitive Position</Tag>
              <h2 className="display-md mt-6">
                Why this business
                <br />
                is built differently.
              </h2>
            </div>
          </div>

          {/* Desktop / tablet grid — hidden below md. 8px gap reads as 6
              discrete tiles vs. one block. */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {POSITION_TILES.map((t, i) => (
              <Reveal key={t.id} delay={i * 60}>
                <PositionCard tile={t} idx={i} />
              </Reveal>
            ))}
          </div>

          {/* Mobile pill / tab pattern */}
          <PositionMobile />
        </div>
      </section>

      {/* Gate form */}
      <section
        id="gate"
        className="di-section"
        style={{ background: "var(--color-surface-container-low)" }}
      >
        <div className="di-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div
              className="label-md inline-flex items-center gap-2 mb-6"
              style={{ color: "var(--color-tertiary)" }}
            >
              <Lock size={12} /> Confidential Information Portal
            </div>
            <h2 className="display-md">Request investor information.</h2>
            <p
              className="mt-6"
              style={{ color: "var(--color-on-surface-variant)", maxWidth: 440 }}
            >
              We share detailed business information with verified enquirers on
              request. Complete the form and our leadership team will be in
              touch directly.
            </p>
            <div className="mt-8 label-sm">
              Submissions route privately to MD and CFO. No auto-reply with
              documents.
            </div>

            <div
              data-testid="investor-direct-contact"
              className="mt-10 pt-8"
              style={{ borderTop: "1px solid var(--color-outline)" }}
            >
              <p
                className="body-sm"
                style={{ color: "var(--color-on-surface-variant)", marginBottom: 16 }}
              >
                If you prefer to speak directly:
              </p>
              <div className="title-lg" style={{ marginBottom: 2 }}>
                Rakesh Jain
              </div>
              <div
                className="label-sm"
                style={{
                  color: "var(--color-on-surface-variant)",
                  marginBottom: 16,
                  textTransform: "none",
                  letterSpacing: 0,
                  fontWeight: 500,
                }}
              >
                Managing Director
              </div>
              <div className="body-sm space-y-1">
                <div>
                  <a
                    href="mailto:md@designindya.com"
                    data-testid="investor-md-email"
                    style={{ color: "var(--color-primary)" }}
                  >
                    md@designindya.com
                  </a>
                </div>
                <div>
                  <a
                    href="tel:+911614642371"
                    data-testid="investor-md-phone"
                    style={{ color: "var(--color-primary)" }}
                  >
                    +91 161 4642371
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            {submitted ? (
              <div
                data-testid="investor-form-success"
                style={{
                  background: "var(--color-surface-container-lowest)",
                  padding: "var(--space-48)",
                  textAlign: "center",
                }}
              >
                <RedDot className="mb-6" />
                <h3 className="headline-lg mb-4">Thank you.</h3>
                <p
                  style={{
                    color: "var(--color-on-surface-variant)",
                    maxWidth: 440,
                    margin: "0 auto",
                  }}
                >
                  Your request has been received. Our MD or CFO will be in
                  touch with you directly, privately, within two business days.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="investor-form"
                style={{
                  background: "var(--color-surface-container-lowest)",
                  padding: "var(--space-48)",
                }}
                className="space-y-6"
              >
                {[
                  { name: "full_name", label: "Full Name", type: "text" },
                  { name: "organisation", label: "Organisation", type: "text" },
                  { name: "role", label: "Role / Title", type: "text" },
                  { name: "email", label: "Email Address", type: "email" },
                ].map((f) => (
                  <div key={f.name}>
                    <label
                      className="label-sm block mb-1"
                      style={{ color: "var(--color-on-surface-variant)" }}
                    >
                      {f.label}
                    </label>
                    <input
                      type={f.type}
                      name={f.name}
                      value={form[f.name]}
                      onChange={onChange}
                      data-testid={`investor-input-${f.name.replace("_", "-")}`}
                      className="di-input"
                      required
                    />
                  </div>
                ))}
                <div>
                  <label
                    className="label-sm block mb-1"
                    style={{ color: "var(--color-on-surface-variant)" }}
                  >
                    Inquiry Note (Purpose of Interest)
                  </label>
                  <textarea
                    name="note"
                    value={form.note}
                    onChange={onChange}
                    data-testid="investor-input-note"
                    rows={3}
                    className="di-input"
                    style={{ resize: "none" }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="investor-submit-btn"
                  className="btn btn-accent w-full justify-center"
                  style={{ marginTop: 16 }}
                >
                  {loading ? "Submitting…" : "Request Investor Information →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Closing — navy panel, headline-only */}
      <section
        className="di-section"
        style={{
          background: "var(--color-primary)",
          color: "var(--color-on-primary)",
          textAlign: "center",
        }}
      >
        <div className="di-container" style={{ maxWidth: 880 }}>
          <h2 className="display-md" style={{ color: "var(--color-on-primary)" }}>
            The foundation is built.
            <br />
            The opportunity is now.
          </h2>
        </div>
      </section>
    </main>
  );
}
